import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { TextArea } from 'lego-on-react';
import { delay } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { ThemePopup } from '../../typings/common';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { FeedbackSurveyFrame } from '../FeedbackSurveyFrame/FeedbackSurveyFrame';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './FeedbackTooltip.css';
import * as keyset from './FeedbackTooltip.i18n';

const i18n = i18nFactory(keyset);

export enum FeedBackTooltipStep {
    promo = 'promo',
    form = 'form',
    survey = 'survey',
    result = 'result'
}

export interface FeedbackTooltipProps {
    showPromo: boolean;
    onSaveClick: (value: string) => void;
    cls?: string;
    isSwitchToOldInterfaceLinkVisible: boolean;
    onSwitchToOldInterfaceLinkClick?: () => void;
}

export interface FeedbackTooltipState {
    isVisible: boolean;
    step: FeedBackTooltipStep;
    messageValue: string;
    isSurveyFinished: boolean;
}

export class FeedbackTooltip extends React.PureComponent<FeedbackTooltipProps, FeedbackTooltipState> {
    private closeTooltipDelay = 1000;

    constructor(props: FeedbackTooltipProps) {
        super(props);

        if (this.props.showPromo) {
            this.state = {
                step: FeedBackTooltipStep.promo,
                isVisible: true,
                messageValue: '',
                isSurveyFinished: false
            };
        } else {
            this.state = {
                step: FeedBackTooltipStep.form, // не survey, потому что DIRECT-90028
                isVisible: false,
                messageValue: '',
                isSurveyFinished: true
            };
        }
    }

    render() {
        const { isVisible, step } = this.state;
        const { cls } = this.props;

        return (
            <>
                <div
                    className={classNames(
                        cls,
                        Bem.cls({
                            block: 'feedback-tooltip',
                            elem: 'icon',
                            mods: { active: isVisible }
                        })
                    )}
                    onClick={this.onIconClick}
                    title={i18n('feedback-icon-hint')}
                >
                    <Shape2
                        icon={Shape2Icon.feedback}
                        theme={Shape2Theme.light}
                        hoverable={true}
                        cls={Bem.cls('feedback-tooltip', 'feedback-icon')}
                        width={21}
                        height={21}
                    />
                </div>

                <PopupDecorator
                    autoclosable={false}
                    target={'anchor'}
                    anchor={this}
                    visible={isVisible}
                    cls={Bem.cls('feedback-tooltip__popup')}
                    directions={['left-top', 'bottom-center']}
                    hasTail={true}
                    theme={ThemePopup.CLEAR}
                    hasClose={step === FeedBackTooltipStep.survey}
                    onClose={this.close}
                >
                    {this.getHeader()}
                    {this.getContent()}
                    {this.getFooter()}
                </PopupDecorator>
            </>
        );
    }

    private getContent = () => {
        const { step, isVisible } = this.state;

        if (!isVisible) {
            return null;
        }

        switch (step) {
            case FeedBackTooltipStep.survey:
                return this.getSurveyContent();
            case FeedBackTooltipStep.promo:
                return this.getPromoContent();
            case FeedBackTooltipStep.form:
                return this.getFormContent();
            case FeedBackTooltipStep.result:
                return this.getOperationResultContent();
            default:
                throw `Не поддерживаемый тип обратной связи ${step}`;
        }
    };

    private getSurveyContent = () => (
        <FeedbackSurveyFrame
            cls={Bem.cls('feedback-tooltip', 'survey-frame')}
            onRendered={this.onFrameRendered}
            onRejected={this.onFrameRejected}
            onFinished={this.onFrameFinished}
        />
    );

    private getFormContent = () => (
        <div className={Bem.cls('feedback-tooltip', 'form-content')}>
            <p
                className={Bem.cls('feedback-tooltip', 'par')}
                dangerouslySetInnerHTML={{ __html: i18n('write-any-feedback') }}
            />
            <TextArea
                theme={'normal'}
                size={'s'}
                cls={Bem.cls('feedback-tooltip', 'field')}
                text={this.state.messageValue}
                onChange={this.onMessageValueChange}
            />
        </div>
    );

    private getPromoContent = () => (
        <div className={Bem.cls('feedback-tooltip', 'promo-content')}>{i18n('promo-content')}</div>
    );

    private getOperationResultContent = () => {
        return (
            <div className={Bem.cls('feedback-tooltip', 'result-content')}>
                <h3 className={Bem.cls('feedback-tooltip', 'result-title')}>{i18n('result-title')}</h3>

                <p className={Bem.cls('feedback-tooltip', 'par')}>{i18n('we-will-connect-to-you-for-questions')}</p>

                <p className={Bem.cls('feedback-tooltip', 'par')}>{i18n('thanks-text')}</p>
            </div>
        );
    };

    private getHeader = () => {
        return (
            <div className={Bem.cls('feedback-tooltip', 'header')}>
                {i18n('title')}
                <div className={Bem.cls('feedback-toolip', 'switch-interface-link')}>
                    {this.getSwitchToOldInterfaceLink()}
                </div>
            </div>
        );
    };

    private getFooter = () => {
        const { step } = this.state;
        let footerContent;

        if (step === FeedBackTooltipStep.promo) {
            footerContent = (
                <>
                    <ButtonDecorator
                        theme={'action'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('write-feedback')}
                        cls={Bem.cls('feedback-tooltip', 'footer-btn')}
                        // https://st.yandex-team.ru/ISL-5279
                        onMouseUp={this.onWriteFeedbackButtonClick}
                    />
                    <ButtonDecorator
                        theme={'pseudo'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('later')}
                        cls={Bem.cls('feedback-tooltip', 'footer-btn')}
                        onClick={this.close}
                    />
                </>
            );
        }

        if (step === FeedBackTooltipStep.form) {
            footerContent = (
                <>
                    <ButtonDecorator
                        theme={'action'}
                        disabled={this.state.messageValue === ''}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('send')}
                        cls={Bem.cls('feedback-tooltip', 'footer-btn')}
                        onClick={this.onSave}
                    />
                    <ButtonDecorator
                        theme={'pseudo'}
                        view="default"
                        tone="default"
                        size={'n'}
                        text={i18n('cancel')}
                        cls={Bem.cls('feedback-tooltip', 'footer-btn')}
                        onClick={this.close}
                    />
                </>
            );
        }

        if (step === FeedBackTooltipStep.result) {
            footerContent = (
                <ButtonDecorator
                    theme={'normal'}
                    size={'n'}
                    text={i18n('close')}
                    cls={Bem.cls('feedback-tooltip', 'footer-btn')}
                    onClick={this.close}
                />
            );
        }

        return footerContent ? <div className={Bem.cls('feedback-tooltip', 'footer')}>{footerContent}</div> : null;
    };

    private getSwitchToOldInterfaceLink() {
        const { onSwitchToOldInterfaceLinkClick, isSwitchToOldInterfaceLinkVisible } = this.props;

        if (!isSwitchToOldInterfaceLinkVisible || onSwitchToOldInterfaceLinkClick === undefined) {
            return null;
        } else {
            return (
                <StyledLink
                    theme={StyledLinkTheme.blue}
                    cls={Bem.cls('feedback-tooltip', 'link-to-old-interface')}
                    onClick={onSwitchToOldInterfaceLinkClick}
                >
                    {i18n('switch-to-old-interface-link')}
                </StyledLink>
            );
        }
    }

    private onIconClick = () => {
        const { isSurveyFinished, isVisible } = this.state;

        if (isVisible) {
            this.close();
        } else {
            this.setState({
                step: isSurveyFinished ? FeedBackTooltipStep.form : FeedBackTooltipStep.survey,
                isVisible: true,
                messageValue: ''
            });
        }
    };

    private onWriteFeedbackButtonClick = () => {
        this.setState({
            step: FeedBackTooltipStep.survey
        });
    };

    private close = () => {
        this.setState({
            isVisible: false
        });
    };

    private onMessageValueChange = (value: string) => {
        this.setState({
            messageValue: value
        });
    };

    private onSave = (event: React.MouseEvent<HTMLElement>) => {
        this.props.onSaveClick(this.state.messageValue);

        this.setState({
            step: FeedBackTooltipStep.result
        });

        event.stopPropagation();
    };

    private onFrameRendered = () => {
        this.setState({
            step: FeedBackTooltipStep.survey
        });
    };

    private onFrameFinished = () => {
        this.setState({
            isSurveyFinished: true
        });
        delay(
            () =>
                this.setState({
                    isVisible: false
                }),
            this.closeTooltipDelay
        );
    };

    private onFrameRejected = () => {
        this.setState({
            step: FeedBackTooltipStep.form
        });
    };
}
